/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { CONTACT_TYPE, CONTRACT_SITUATION } from 'app-constants'
import { formatPhoneToDB } from 'utils'
import { contactService, contractService, customerService } from 'services'

const CUSTOMER_INVOICES_INITIAL_STATE = {
  isLoading: true,
  content: [],
  hasMore: false,
  page: 0,
}

const CUSTOMER_PAID_BILLS_INITIAL_STATE = {
  isLoading: true,
  content: [],
  hasMore: false,
  page: 0,
}

const initialState = {
  contracts: null,
  info: {
    contacts: [],
    document: '',
    name: '',
    password: '',
    isFirstAccess: true,
    customerId: null,
  },
  financial: {
    bankSlips: {
      isLoading: true,
      content: [],
      hasPendingNegotiated: false,
      daysToBreachOfAgreement: null,
    },
    invoices: { ...CUSTOMER_INVOICES_INITIAL_STATE },
    paid: { ...CUSTOMER_PAID_BILLS_INITIAL_STATE },
  },
}

const { getContracts } = contractService()
const {
  addContact,
  getContacts,
  deleteContact,
  updateContact,
  confirmCellphone,
  updatePhone,
  updateEmail,
} = contactService()
const { getCustomer } = customerService()

export const fetchContracts = createAsyncThunk('customer/fetchContracts', async () => {
  const {
    data: { name, contracts },
  } = await getContracts()

  const sortedContracts = contracts.sort((a, b) => {
    if (a.situation === b.situation) {
      return 0
    }
    if (a.situation === CONTRACT_SITUATION.ENABLED) {
      return -1
    }
    if (b.situation === CONTRACT_SITUATION.ENABLED) {
      return 1
    }

    return 0
  })

  return { name, contracts: sortedContracts }
})

export const fetchCustomer = createAsyncThunk('customer/fetchCustomer', async () => {
  const { data } = await getCustomer()

  return data
})

export const fetchContacts = createAsyncThunk('customer/fetchContacts', async () => {
  const { data } = await getContacts()
  return data
})

export const updateCustomerContact = createAsyncThunk(
  'customer/updateCustomerContact',
  async customer => {
    const { contactId, contact, contactType, valid, primary } = customer
    await updateContact(contactId, { contact, contactType, valid, primary })
  }
)

export const confirmCustomerPhone = createAsyncThunk(
  'customer/confirmCustomerPhone',
  async ({ contactId, code, phone }, { rejectWithValue }) => {
    try {
      await confirmCellphone(code, formatPhoneToDB(phone))

      return { contactId }
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const addCustomerContact = createAsyncThunk(
  'customer/addCustomerContact',
  async ({ contactType, contact, valid }, { rejectWithValue }) => {
    try {
      const { data } = await addContact({ contactType, contact, valid })

      return { newContactId: data.newContactId, contactType, contact }
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const deleteCustomerContact = createAsyncThunk(
  'customer/deleteCustomerContact',
  async ({ contact, contactType, contactId }) => {
    await deleteContact({ contact, contactType })

    return { contactId }
  }
)

export const setCustomerMainContact = createAsyncThunk(
  'customer/setCustomerMainContact',
  async ({ contact }, { rejectWithValue }) => {
    try {
      const isPhone = CONTACT_TYPE.PHONE === contact.contactType
      const { contactId } = contact

      if (isPhone) await updatePhone(contactId, { isMain: true })
      else await updateEmail(contactId, { isMain: true })

      return { contactId }
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    handlePasswordRecovery: (state, action) => {
      state.passwordRecovery = action.payload
    },
    handleContractChange: state => {
      state.financial.invoices = CUSTOMER_INVOICES_INITIAL_STATE
      state.financial.paid = CUSTOMER_PAID_BILLS_INITIAL_STATE
    },
    handleNewContact: (state, action) => {
      state.info.contacts.push(action.payload)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchContracts.fulfilled, (state, action) => {
        const { name, contracts } = action.payload
        state.contracts = contracts
        state.info.name = name
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        const { customerId, document } = action.payload
        state.info.customerId = customerId
        state.info.document = document
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.info.contacts = action.payload
      })
      .addCase(updateCustomerContact.fulfilled, (state, action) => {
        const contactToBeUpdated = state.info.contacts.find(
          contact => contact.contactId === action.meta.arg.contactId
        )

        contactToBeUpdated.confirmed = false
        contactToBeUpdated.contact = action.meta.arg.contact
        contactToBeUpdated.contactType = action.meta.arg.contactType
        contactToBeUpdated.valid = action.meta.arg.valid
      })
      .addCase(deleteCustomerContact.fulfilled, (state, action) => {
        state.info.contacts = state.info.contacts.filter(
          contact => contact.contactId !== action.payload.contactId
        )
      })
      .addCase(setCustomerMainContact.fulfilled, (state, action) => {
        const newMainContact = state.info.contacts.find(
          contact => contact.contactId === action.payload.contactId
        )

        const oldMainContact = state.info.contacts.find(
          contact => contact.principal && contact.contactType === newMainContact.contactType
        )
        oldMainContact.principal = false
        newMainContact.principal = true
      })
  },
})

export const {
  handlePasswordRecovery,
  handleContractChange,
  handleNewContact,
} = customerSlice.actions

export default customerSlice.reducer
